import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { STATUS_STEPS } from "@/lib/api";
import { StatusBadge } from "@/components/StatusBadge";
import { ArrowRight, LayoutGrid, Wallet, Clapperboard, CheckCircle2 } from "lucide-react";

export default function AdminDashboard() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    api.get("/projects").then((r) => setProjects(r.data)).finally(() => setLoading(false));
  }, []);

  const stats = {
    total: projects.length,
    awaiting_quote: projects.filter((p) => p.status === "approved").length,
    paid: projects.filter((p) => ["paid", "in_production", "delivered"].includes(p.status)).length,
    revenue: projects.filter((p) => ["paid", "in_production", "delivered"].includes(p.status)).reduce((s, p) => s + (p.deposit || 0), 0),
  };

  const filtered = filter === "all" ? projects : projects.filter((p) => p.status === filter);

  const cards = [
    { label: "Toplam Proje", value: stats.total, icon: LayoutGrid },
    { label: "Teklif Bekleyen", value: stats.awaiting_quote, icon: Clapperboard },
    { label: "Ödemeli Proje", value: stats.paid, icon: CheckCircle2 },
    { label: "Tahsil Edilen", value: `${stats.revenue.toLocaleString("tr-TR")} ₺`, icon: Wallet },
  ];

  return (
    <div className="max-w-7xl mx-auto px-6 py-12">
      <div className="flex flex-wrap items-start justify-between gap-4 mb-10">
        <div>
          <p className="text-sm uppercase tracking-[0.3em] text-gold mb-2">Stüdyo Yönetimi</p>
          <h1 className="font-serif text-4xl lg:text-5xl">Prodüksiyon Paneli</h1>
        </div>
        <Link to="/admin/content" data-testid="admin-content-link"
          className="inline-flex items-center gap-2 border border-gold/40 text-gold hover:bg-gold hover:text-black transition-colors rounded-md h-11 px-5 text-sm">
          Site İçeriği & SEO
        </Link>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10" data-testid="admin-stats">
        {cards.map((c) => (
          <div key={c.label} className="bg-[#0A0A0A] border border-white/5 rounded-xl p-5 hover:border-gold/30 transition-colors">
            <c.icon className="w-5 h-5 text-gold mb-3" />
            <p className="text-2xl font-serif text-white">{c.value}</p>
            <p className="text-xs uppercase tracking-widest text-white/40 mt-1">{c.label}</p>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap gap-2 mb-6">
        <button onClick={() => setFilter("all")} data-testid="filter-all"
          className={`text-xs uppercase tracking-wider px-4 py-2 rounded-full border transition-colors ${filter === "all" ? "bg-gold text-black border-gold" : "border-white/15 text-white/60 hover:border-gold/40"}`}>
          Tümü
        </button>
        {STATUS_STEPS.map((s) => (
          <button key={s.key} onClick={() => setFilter(s.key)} data-testid={`filter-${s.key}`}
            className={`text-xs uppercase tracking-wider px-4 py-2 rounded-full border transition-colors ${filter === s.key ? "bg-gold text-black border-gold" : "border-white/15 text-white/60 hover:border-gold/40"}`}>
            {s.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-white/40 animate-pulse">Yükleniyor…</div>
      ) : filtered.length === 0 ? (
        <div className="bg-[#0A0A0A] border border-dashed border-white/10 rounded-xl p-14 text-center text-white/40">
          Bu durumda proje yok.
        </div>
      ) : (
        <div className="border border-white/5 rounded-xl overflow-hidden" data-testid="admin-projects-table">
          {filtered.map((p) => (
            <Link key={p.id} to={`/admin/project/${p.id}`} data-testid={`admin-project-row-${p.id}`}
              className="flex items-center justify-between gap-4 px-5 py-4 border-b border-white/5 last:border-0 hover:bg-white/5 transition-colors group">
              <div className="min-w-0 flex-1">
                <p className="font-serif text-xl truncate">{p.title}</p>
                <p className="text-sm text-white/40 truncate">{p.customer_name} • {p.customer_email}</p>
              </div>
              <div className="hidden sm:block text-right">
                {p.price_total ? <span className="text-gold text-sm">{p.price_total.toLocaleString("tr-TR")} ₺</span> : <span className="text-white/20 text-sm">—</span>}
              </div>
              <StatusBadge status={p.status} />
              <ArrowRight className="w-4 h-4 text-white/30 group-hover:text-gold transition-colors" />
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
